// eslint-disable-next-line jira/filename-case
import React, { useEffect, useState } from 'react';
import { useCommandPaletteShortcut } from '@jira/compal__common/src/controllers/use-command-palette-shortcut/index.tsx';
import { useCommandPaletteIsOpen } from '@jira/compal__command-palette/src/controllers/command-palette/index.tsx';
import type { CommandPaletteModalProps } from '@jira/compal__command-palette/src/ui/modal/types.tsx';
import { CommandPaletteWrapper } from '@jira/compal__command-palette/src/ui/wrapper/index.tsx';
import Shortcuts from '@jira/platform__common-components-keyboard-shortcuts/src/shortcuts/index.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import usePageLoadedSubscriber from '@atlassian/jira-spa-state-controller/src/components/page-loaded-state/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { lazy, type LazyComponent } from '@atlassian/react-loosely-lazy';
import { JiraCommandPaletteErrorBoundary } from './common/ui/error-boundary/index.tsx';

/* istanbul ignore next */

export const JiraCommandPaletteLazy: LazyComponent<
	(props: CommandPaletteModalProps) => React.JSX.Element
> = // eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
	lazy(() =>
		import(/* webpackChunkName: "async-command-palette-integration" */ './ui/index.tsx').then(
			(module) => module.CommandPaletteIntegration,
		),
	);

const InternalAsyncCommandPalette = (props: CommandPaletteModalProps) => {
	const [initialised, setInitialised] = useState(false);

	const [{ isOpen }] = useCommandPaletteIsOpen();
	const { keyMap: commandPaletteKeyMap } = useCommandPaletteShortcut();

	const [{ hasLoaded }] = usePageLoadedSubscriber();

	useEffect(() => {
		if (!initialised && (isOpen || hasLoaded)) setInitialised(true);
	}, [initialised, isOpen, hasLoaded]);

	return (
		<UFOSegment name="command-palette">
			<Shortcuts keyMap={commandPaletteKeyMap} />

			{initialised && (
				<Placeholder name="jira-command-palette" fallback={<></>}>
					<JiraCommandPaletteLazy {...props} />
				</Placeholder>
			)}
		</UFOSegment>
	);
};

export const AsyncJiraCommandPalette = (props: CommandPaletteModalProps): React.JSX.Element => (
	<JiraCommandPaletteErrorBoundary id="jira-command-palette-integration-async">
		<CommandPaletteWrapper>
			<InternalAsyncCommandPalette {...props} />
		</CommandPaletteWrapper>
	</JiraCommandPaletteErrorBoundary>
);
